import {Children, isValidElement, ReactElement, ReactNode, useState} from 'react';
import clsx from 'clsx';
import Label from 'components/basic/Label';
import {Link} from 'react-router-dom';
import {Button} from '@/shadcn_components/ui/button';
import {InputProps, useInput} from '@/hooks/useInput';
import {isShallowEqual} from '@/lib/utils';
import {XIcon} from 'lucide-react';

export type FileInputProps = InputProps & {
  //   accept?: DropzoneOptions['accept'];
  accept?: string;
  className?: string;
  children?: ReactNode;
  // labelMultiple?: string;
  // labelSingle?: string;
  multiple?: boolean;
  dropzonePrompt?: ReactNode;
  renderPreview?: (files: any[], options?: RenderPreviewOptions) => ReactNode;
  //   maxSize?: DropzoneOptions['maxSize'];
  //   minSize?: DropzoneOptions['minSize'];
  //   multiple?: DropzoneOptions['multiple'];
  //   options?: DropzoneOptions;
  //   onRemove?: Function;
  placeholder?: ReactNode;
  //   removeIcon?: FC<SvgIconProps>;
  inputProps?: any;
  validateFileRemoval?(file): boolean | Promise<boolean>;
  //   sx?: SxProps;
};

export type RenderPreviewOptions = {
  removeFile?: (f: any) => void;
};

export const FileInput = (props: FileInputProps) => {
  const {
    accept,
    children,
    className,
    format,
    helperText,
    inputProps: inputPropsOptions,
    // maxSize,
    // minSize,
    multiple = false,
    label,
    // options = {},
    // onRemove: onRemoveProp,
    parse,
    placeholder,
    // removeIcon,
    // resource,
    source,
    // validate,
    validateFileRemoval,
    dropzonePrompt,
    renderPreview = defaultRenderPreview,
    ...rest
  } = props;
  //   const {onDrop: onDropProp} = options;
  // const translate = useTranslate();

  // turn a browser dropped file structure into expected structure
  const transformFile = file => {
    // not a input file (from local file system)
    if (!(file instanceof File)) {
      return {
        ...file,
        // convert from attachment (from server) to file (for preview)
        src: file.src || file.url,
        title: file.title || file.name,
      };
    }

    const preview = URL.createObjectURL(file);
    const transformedFile = {
      rawFile: file,
      src: preview,
      title: file.name,
    };

    return transformedFile;
  };

  const transformFiles = (files: any[]) => {
    if (!files) {
      return multiple ? [] : null;
    }

    if (Array.isArray(files)) {
      return files.map(transformFile);
    }

    return transformFile(files);
  };

  const {
    field: {onChange, onBlur, value},
    fieldState,
    formState: {isSubmitted},
  } = useInput({
    format: format || transformFiles,
    parse: parse || transformFiles,
    source,
    ...rest,
  });
  const {isTouched, error, invalid} = fieldState;
  const files = value ? (Array.isArray(value) ? value : [value]) : [];

  const onDrop = newFiles => {
    const updatedFiles = multiple ? [...files, ...newFiles] : [...newFiles];

    if (multiple) {
      onChange(updatedFiles);
      onBlur();
    } else {
      onChange(updatedFiles[0]);
      onBlur();
    }

    // if (onDropProp) {
    //   onDropProp(newFiles, rejectedFiles, event);
    // }
  };

  const onRemove = file => {
    if (multiple) {
      const filteredFiles = files.filter(stateFile => !isShallowEqual(stateFile, file));
      onChange(filteredFiles as any);
      onBlur();
    } else {
      onChange(null);
      onBlur();
    }

    // if (onRemoveProp) {
    //   onRemoveProp(file);
    // }
  };

  const childrenElement =
    children && isValidElement(Children.only(children))
      ? (Children.only(children) as ReactElement<any>)
      : undefined;

  const [dragDropState, setDragDropState] = useState({
    isDragActive: false,
  });

  //   const renderHelperText = helperText !== false || ((isTouched || isSubmitted) && invalid);

  return (
    <div className={clsx('form-control w-full', className)}>
      <Label>{props.label}</Label>
      {/* dropzone */}
      <div
        id="dropzone"
        className={clsx(
          'w-[400px] mx-auto relative border-2 border-gray-300 border-dashed rounded-lg p-2 flex flex-col items-center',
          {
            'border-indigo-600 bg-indigo-50': dragDropState.isDragActive,
          },
        )}
        onDragOver={e => {
          e.preventDefault();
          setDragDropState({...dragDropState, isDragActive: true});
        }}
        onDragLeave={e => {
          e.preventDefault();
          setDragDropState({...dragDropState, isDragActive: false});
        }}
        onDrop={e => {
          e.preventDefault();
          setDragDropState({...dragDropState, isDragActive: false});
          onDrop(e.dataTransfer.files);
        }}>
        <input
          multiple={multiple}
          type="file"
          className="absolute inset-0 w-full h-full opacity-0 z-50"
          onChange={e => {
            // console.log('input1', e);
            onDrop(e.target.files);
          }}
        />
        {dropzonePrompt ? (
          dropzonePrompt
        ) : (
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            Kéo thả vào đây hoặc nhấn để chọn file tải lên
          </h3>
        )}
      </div>
      {/* file preview */}
      {renderPreview?.(files, {removeFile: onRemove})}
    </div>
  );
};

function defaultRenderPreview(files, options?: RenderPreviewOptions) {
  return (
    <div className="flex flex-col">
      {files.map((f, i) => (
        <div key={i} className="flex flex-row w-max items-center">
          <Link key={i} to={f.src} target="_blank" className="text-blue-500 underline">
            {f.title}
          </Link>
          <Button type='button' variant="ghost" size={'icon'} onClick={() => options?.removeFile?.(f)}>
            <XIcon className="w-4 h-4" />
          </Button>
        </div>
      ))}
    </div>
  );
}
